import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../lib/api-service';
import Logo from '../components/Logo';
import toast from 'react-hot-toast';

// Helper function to validate password strength
const isPasswordValid = (password) => {
  if (!password || password.length < 12) return false;
  if (!/[A-Z]/.test(password)) return false;
  if (!/[a-z]/.test(password)) return false;
  if (!/[0-9]/.test(password)) return false;
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) return false;
  return true;
};

// Shared Components
const InputField = ({ label, name, type, value, onChange, error, placeholder, icon, required }) => (
  <div>
    <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
        {icon}
      </div>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full pl-12 pr-4 py-3.5 border-2 rounded-xl text-base focus:outline-none focus:border-purple-500 focus:ring-4 focus:ring-purple-500/20 transition-all duration-200"
        style={{
          backgroundColor: 'var(--color-bg-tertiary)',
          borderColor: 'var(--color-border-default)',
          color: 'var(--color-text-primary)'
        }}
      />
    </div>
    {error && (
      <p className="mt-2 text-sm text-red-400">{error}</p>
    )}
  </div>
);

const PasswordField = ({ label, name, value, onChange, error, placeholder, showPassword, toggleShowPassword, helperText, required, rememberMe, onRememberMeChange, showRememberMe, showStrength, showMatch, matchValue }) => {
  const isValid = showStrength && value && isPasswordValid(value);
  const isMatching = showMatch && value && matchValue && value === matchValue && value.length > 0;
  const hasValue = value && value.length > 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>
          {label} {required && <span className="text-red-400">*</span>}
        </label>
        {showRememberMe && (
          <div className="flex items-center">
            <div className="relative flex items-center">
              <input
                type="checkbox"
                id={`rememberMe-${name}`}
                checked={rememberMe}
                onChange={(e) => onRememberMeChange(e.target.checked)}
                tabIndex="-1"
                className="w-3.5 h-3.5 rounded cursor-pointer appearance-none border-2 checked:bg-purple-400 checked:border-purple-400 focus:ring-2 focus:ring-purple-500/30 transition-colors"
                style={{ color: 'var(--color-text-on-accent)' }}
                style={{
                  borderColor: 'var(--color-border-default)'
                }}
              />
              {rememberMe && (
                <svg className="absolute left-0 w-3.5 h-3.5 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="#ffffff" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
            <label htmlFor={`rememberMe-${name}`} className="ml-1.5 text-xs cursor-pointer select-none" style={{ color: 'var(--color-text-muted)' }}>
              Remember me
            </label>
          </div>
        )}
      </div>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <svg className="h-5 w-5" style={{ color: 'var(--color-text-muted)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <input
          type={showPassword ? "text" : "password"}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={`w-full pl-12 pr-12 py-3.5 border-2 rounded-xl text-base focus:outline-none focus:ring-4 transition-all duration-200 ${
            isValid || isMatching
              ? 'border-green-600 focus:border-green-600 focus:ring-green-600/20'
              : error
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
              : 'focus:border-purple-500 focus:ring-purple-500/20'
          }`}
          style={
            !isValid && !isMatching && !error
              ? {
                  backgroundColor: 'var(--color-bg-tertiary)',
                  borderColor: 'var(--color-border-default)',
                  color: 'var(--color-text-primary)'
                }
              : {}
          }
        />
        <button
          type="button"
          onClick={toggleShowPassword}
          tabIndex="-1"
          className="absolute inset-y-0 right-0 pr-4 flex items-center transition-colors"
          style={{ color: 'var(--color-text-muted)' }}
          onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-text-secondary)'}
          onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-text-muted)'}
        >
          {showPassword ? (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
            </svg>
          ) : (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          )}
        </button>
      </div>
      {error && (
        <p className="mt-2 text-sm text-red-400">{error}</p>
      )}
      {!error && isValid && (
        <div className="mt-2 flex items-center text-sm text-green-600">
          <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Password meets all requirements
        </div>
      )}
      {!error && isMatching && (
        <div className="mt-2 flex items-center text-sm text-green-600">
          <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Passwords match
        </div>
      )}
      {!error && !isValid && !isMatching && helperText && hasValue && (
        <p className="mt-2 text-xs" style={{ color: 'var(--color-text-muted)' }}>{helperText}</p>
      )}
      {!error && !isValid && !isMatching && helperText && !hasValue && (
        <p className="mt-2 text-xs" style={{ color: 'var(--color-text-muted)' }}>{helperText}</p>
      )}
    </div>
  );
};

const UserIcon = () => (
  <svg className="h-5 w-5" style={{ color: 'var(--color-text-muted)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

export default function Login() {
  const [searchParams] = useSearchParams();
  const inviteParam = searchParams.get('invite');
  const modeParam = searchParams.get('mode');

  const [isLogin, setIsLogin] = useState(!inviteParam && modeParam !== 'register'); // Start in register mode if invite token present or mode=register
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    inviteToken: inviteParam || '',
  });
  const [inviteInfo, setInviteInfo] = useState(null);
  const [validatingInvite, setValidatingInvite] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const { login, register, registerOrganization } = useAuth();
  const navigate = useNavigate();

  // Validate invitation token on mount if present
  useEffect(() => {
    if (inviteParam) {
      validateInviteToken(inviteParam);
    }
  }, [inviteParam]);

  const validateInviteToken = async (token) => {
    setValidatingInvite(true);
    try {
      const response = await authApi.validateInvitation(token);
      if (response.is_valid) {
        setInviteInfo(response);
        // Pre-fill email if provided in invitation
        if (response.email) {
          setFormData(prev => ({ ...prev, email: response.email }));
        }
        toast.success(`Welcome to ${response.company_name}!`);
      } else {
        toast.error(response.message);
        setFormData(prev => ({ ...prev, inviteToken: '' }));
      }
    } catch (error) {
      console.error('Failed to validate invitation:', error);
      toast.error('Invalid invitation link');
      setFormData(prev => ({ ...prev, inviteToken: '' }));
    } finally {
      setValidatingInvite(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 12) {
      newErrors.password = 'Password must be at least 12 characters';
    } else if (!/[A-Z]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter';
    } else if (!/[a-z]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one lowercase letter';
    } else if (!/[0-9]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one number';
    } else if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one special character';
    }

    if (!isLogin) {
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      let result;
      if (isLogin) {
        result = await login(formData.username, formData.password, rememberMe);
      } else {
        // New Organization flow - no invite token needed
        result = await registerOrganization(
          formData.username,
          formData.password,
          'My Organization',  // Default organization name
          rememberMe
        );
      }

      if (result.success) {
        navigate('/home');
      } else {
        // Clear username and password fields on failed login
        if (isLogin) {
          setFormData((prev) => ({
            ...prev,
            username: '',
            password: '',
          }));
        }
      }
    } catch (err) {
      // Error already handled by API client
      // Clear username and password fields on error
      if (isLogin) {
        setFormData((prev) => ({
          ...prev,
          username: '',
          password: '',
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    // Preserve username, password, and confirmPassword when switching tabs
    setFormData({
      username: formData.username,
      email: formData.email,
      password: formData.password,
      confirmPassword: formData.confirmPassword,
      fullName: formData.fullName,
      inviteToken: formData.inviteToken,
    });
    setErrors({});
    // Hide passwords when switching tabs for security
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  return (
    <div className="h-screen w-screen flex items-center justify-center p-4 sm:p-6 overflow-hidden" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      {/* Animated background accents */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-64 h-64 sm:w-96 sm:h-96 bg-purple-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-64 h-64 sm:w-96 sm:h-96 bg-blue-600/10 rounded-full blur-3xl"></div>
      </div>

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px] pointer-events-none"></div>

      {/* Login Container */}
      <div className="w-full max-w-md lg:max-w-lg xl:max-w-xl relative z-10">
        {/* Card with gradient border effect */}
        <div className="relative group">
          {/* Gradient border glow */}
          <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl sm:rounded-3xl blur opacity-30 group-hover:opacity-50 transition duration-1000"></div>

          {/* Main card */}
          <div className="relative rounded-2xl sm:rounded-3xl shadow-2xl border" style={{ backgroundColor: 'var(--color-bg-secondary)', borderColor: 'var(--color-border-default)' }}>

            {/* Tab Switcher */}
            <div className="flex border-b" style={{ borderColor: 'var(--color-border-default)' }}>
              <button
                type="button"
                onClick={() => {
                  if (!isLogin) toggleMode();
                }}
                className={`flex-1 py-4 px-6 text-base font-semibold transition-all duration-200 rounded-tl-2xl sm:rounded-tl-3xl ${
                  isLogin
                    ? 'border-b-2 border-purple-500'
                    : ''
                }`}
                style={
                  isLogin
                    ? { backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-primary)' }
                    : { backgroundColor: 'transparent', color: 'var(--color-text-muted)' }
                }
                onMouseEnter={(e) => {
                  if (!isLogin) {
                    e.currentTarget.style.color = 'var(--color-text-secondary)';
                    e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isLogin) {
                    e.currentTarget.style.color = 'var(--color-text-muted)';
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                Login
              </button>
              <button
                type="button"
                onClick={() => {
                  if (isLogin) toggleMode();
                }}
                className={`flex-1 py-4 px-6 text-base font-semibold transition-all duration-200 rounded-tr-2xl sm:rounded-tr-3xl ${
                  !isLogin
                    ? 'border-b-2 border-purple-500'
                    : ''
                }`}
                style={
                  !isLogin
                    ? { backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-primary)' }
                    : { backgroundColor: 'transparent', color: 'var(--color-text-muted)' }
                }
                onMouseEnter={(e) => {
                  if (isLogin) {
                    e.currentTarget.style.color = 'var(--color-text-secondary)';
                    e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (isLogin) {
                    e.currentTarget.style.color = 'var(--color-text-muted)';
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                New Organization
              </button>
            </div>

            {/* Form Container */}
            <div className="p-8 sm:p-10 lg:p-12">
              {/* Logo/Icon */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center mb-4">
                  <Logo className="h-20" />
                </div>
                <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent mb-2 pb-1">
                  {isLogin ? 'Welcome Back' : 'Create Organization'}
                </h1>
                <p className="text-sm sm:text-base" style={{ color: 'var(--color-text-muted)' }}>
                  {isLogin ? 'Sign in to your account' : 'Start your knowledge management journey'}
                </p>
              </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">

              {/* Login Fields Container */}
              {isLogin ? (
                <div className="border-2 rounded-xl p-6 space-y-5" style={{ borderColor: 'var(--color-border-default)', backgroundColor: 'var(--color-bg-tertiary)' }}>
                  {/* Username Input */}
                  <InputField
                    label="Username"
                    name="username"
                    type="text"
                    value={formData.username}
                    onChange={handleChange}
                    error={errors.username}
                    placeholder="Enter your username"
                    icon={<UserIcon />}
                  />

                  {/* Password Input */}
                  <PasswordField
                    label="Password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    error={errors.password}
                    placeholder="Enter your password"
                    showPassword={showPassword}
                    toggleShowPassword={() => setShowPassword(!showPassword)}
                    showRememberMe={true}
                    rememberMe={rememberMe}
                    onRememberMeChange={setRememberMe}
                  />
                </div>
              ) : (
                <div className="border-2 rounded-xl p-6 space-y-5" style={{ borderColor: 'var(--color-border-default)', backgroundColor: 'var(--color-bg-tertiary)' }}>
                  {/* Username Input */}
                  <InputField
                    label="Username"
                    name="username"
                    type="text"
                    value={formData.username}
                    onChange={handleChange}
                    error={errors.username}
                    placeholder="Enter your username"
                    icon={<UserIcon />}
                    required={true}
                  />

                  {/* Password Input */}
                  <PasswordField
                    label="Password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    error={errors.password}
                    placeholder="Enter your password"
                    showPassword={showPassword}
                    toggleShowPassword={() => setShowPassword(!showPassword)}
                    helperText="Min 12 characters, 1 uppercase, 1 lowercase, 1 number, 1 special character"
                    required={true}
                    showRememberMe={false}
                    showStrength={true}
                  />

                  {/* Confirm Password Input */}
                  <PasswordField
                    label="Confirm Password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    error={errors.confirmPassword}
                    placeholder="Re-enter your password"
                    showPassword={showConfirmPassword}
                    toggleShowPassword={() => setShowConfirmPassword(!showConfirmPassword)}
                    required={true}
                    showRememberMe={true}
                    rememberMe={rememberMe}
                    onRememberMeChange={setRememberMe}
                    showMatch={true}
                    matchValue={formData.password}
                  />
                </div>
              )}

              {/* Account Recovery Warning (New Organization only) */}
              {!isLogin && (
                <div className="border rounded-xl p-4" style={{ backgroundColor: 'var(--color-bg-tertiary)', borderColor: 'var(--color-border-default)' }}>
                  <div className="flex items-start space-x-3">
                    <svg className="h-5 w-5 text-orange-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <p className="text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Account Recovery Is Not Possible</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full mt-6 py-3.5 px-6 rounded-xl text-base font-semibold bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-4 focus:ring-purple-500/50 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                style={{ color: 'var(--color-text-on-accent)' }}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {isLogin ? 'Signing in...' : 'Creating organization...'}
                  </span>
                ) : (
                  isLogin ? 'Sign In' : 'Create Organization'
                )}
              </button>
            </form>
            </div>
          </div>
        </div>

        {/* Footer Text */}
        <div className="flex items-center justify-center mt-6 gap-2">
          <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Powered by</span>
          <Logo className="h-8" />
        </div>
      </div>
    </div>
  );
}
